import React, { useState, useEffect } from 'react';
import {
  getAvailableSkills,
  createScheduledTask,
  AvailableSkill,
  ScheduleConfig,
  IoReconciliationConfig,
} from '../../services/scheduledTasksApi';
import EmailDigestConfig, { EmailDigestConfigData } from './EmailDigestConfig';
import ReportConfig, { ReportConfigData } from './ReportConfig';
import SlackConfig, { SlackConfigData } from './SlackConfig';
import { getSkillDisplay } from './skillDisplayMeta';
import { auth } from '../../config/firebase';
import { Button, Modal, Spinner } from '../ui';
import { formFieldStyles as ff } from './formFieldStyles';

interface CreateScheduledTaskModalProps {
  onClose: () => void;
  onSuccess: () => void;
  showError?: (notification: any) => void;
}

const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Phoenix', label: 'Arizona (No DST)' },
  { value: 'America/Anchorage', label: 'Alaska Time' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time' },
  { value: 'UTC', label: 'UTC' },
];

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sun', fullLabel: 'Sunday' },
  { value: 1, label: 'Mon', fullLabel: 'Monday' },
  { value: 2, label: 'Tue', fullLabel: 'Tuesday' },
  { value: 3, label: 'Wed', fullLabel: 'Wednesday' },
  { value: 4, label: 'Thu', fullLabel: 'Thursday' },
  { value: 5, label: 'Fri', fullLabel: 'Friday' },
  { value: 6, label: 'Sat', fullLabel: 'Saturday' },
];

// Get today's date as YYYY-MM-DD in local time (not UTC)
const getLocalDateString = (date: Date = new Date()): string => {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

// Skills that don't need channel name input
const SKILLS_WITHOUT_CHANNEL = ['ad-ops-workflows', 'email-digest', 'network-performance-report', 'io-reconciliation', 'slack-summary'];

// Skills that need a per-task prompt because they're ad-hoc by nature
// (drafting an email to a specific brand or contact, not a recurring report).
const SKILLS_REQUIRING_PROMPT: Record<string, { label: string; placeholder: string; helper: string }> = {
  'outreach-intelligence': {
    label: 'What should this email cover?',
    placeholder: "e.g. \"E1 cold outreach to Acme Foods (consumer packaged goods) — pitch our Q3 cooking creators package, lead with our reach numbers in their target audience.\"",
    helper: 'Tell the assistant who you\'re emailing, what stage of the sequence (E1 cold, E2–E5 follow-up, or a response), and any context (campaign, creators, prior touch). The MOI framework uses this to draft the email.',
  },
  'partner-outreach': {
    label: 'What should this email cover?',
    placeholder: "e.g. \"E2 follow-up to Maya at Greenline Studios — she hasn't replied to my intro from two weeks ago. Lightly nudge, reshare the partnership deck.\"",
    helper: 'Tell the assistant which partner contact, what stage (E1 cold, E2–E5 follow-up, or a response), and any context (prior touch, deal stage, attachments). The MOI framework uses this to draft the email.',
  },
};

const CreateScheduledTaskModal: React.FC<CreateScheduledTaskModalProps> = ({
  onClose,
  onSuccess,
  showError,
}) => {
  const [skills, setSkills] = useState<AvailableSkill[]>([]);
  const [loadingSkills, setLoadingSkills] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [selectedSkill, setSelectedSkill] = useState<string>('');
  const [prompt, setPrompt] = useState<string>('');
  const [channelName, setChannelName] = useState<string>('');
  const [scheduleType, setScheduleType] = useState<'once' | 'weekly' | 'monthly'>('weekly');
  const [time, setTime] = useState<string>('09:00');
  const [timezone, setTimezone] = useState<string>('America/New_York');
  const [daysOfWeek, setDaysOfWeek] = useState<number[]>([1]); // Default to Monday
  const [dayOfMonth, setDayOfMonth] = useState<number>(1);
  const [runDate, setRunDate] = useState<string>(() => getLocalDateString());

  // Helper to check if a skill needs channel name
  const skillNeedsChannel = (skillName: string): boolean => {
    return !SKILLS_WITHOUT_CHANNEL.includes(skillName);
  };

  // Toggle day selection
  const toggleDayOfWeek = (day: number) => {
    setDaysOfWeek(prev => {
      if (prev.includes(day)) {
        // Don't allow removing the last day
        if (prev.length === 1) return prev;
        return prev.filter(d => d !== day);
      } else {
        return [...prev, day].sort((a, b) => a - b);
      }
    });
  };

  // Custom email subject (for non-email-digest tasks; email-digest has its own in EmailDigestConfig)
  const [customSubject, setCustomSubject] = useState('');

  // Email Digest specific config
  const [emailDigestConfig, setEmailDigestConfig] = useState<EmailDigestConfigData>({
    sourceEmail: 'creators@culturegenesis.com',
    recipients: [],
    daysBack: 7,
    includeAttachments: false,
  });

  // Report specific config (for network-performance-report)
  const [reportConfig, setReportConfig] = useState<ReportConfigData>({
    recipients: [],
    topN: 40,
    includeAllDef: true,
  });

  // IO Reconciliation specific config
  const [ioTestMode, setIoTestMode] = useState(false);
  const [ioTestEmail, setIoTestEmail] = useState('');

  // Slack Summary specific config
  const [slackConfig, setSlackConfig] = useState<SlackConfigData>({
    scope: 'mentions-and-dms',
    channelIds: [],
    daysBack: 7,
  });

  // Fetch available skills on mount
  useEffect(() => {
    const fetchSkills = async () => {
      try {
        const fetchedSkills = await getAvailableSkills();
        setSkills(fetchedSkills);
        if (fetchedSkills.length > 0) {
          setSelectedSkill(fetchedSkills[0].name);
        }
      } catch (err: any) {
        showError?.({ title: 'Error', message: 'Failed to load available skills' });
      } finally {
        setLoadingSkills(false);
      }
    };
    fetchSkills();
  }, [showError]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedSkill) {
      showError?.({ title: 'Validation Error', message: 'Please select a skill' });
      return;
    }

    if (SKILLS_REQUIRING_PROMPT[selectedSkill] && !prompt.trim()) {
      showError?.({
        title: 'Instructions needed',
        message: 'This task is ad-hoc by nature — please describe who the email is for and what it should cover.',
      });
      return;
    }

    // Validate IO Reconciliation test email if test mode is on
    if (selectedSkill === 'io-reconciliation' && ioTestMode) {
      if (!ioTestEmail.trim()) {
        showError?.({ title: 'Validation Error', message: 'Please enter a test email address' });
        return;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(ioTestEmail.trim())) {
        showError?.({ title: 'Validation Error', message: 'Please enter a valid email address' });
        return;
      }
    }

    // Validate email digest config if skill is email-digest
    if (selectedSkill === 'email-digest') {
      if (!emailDigestConfig.sourceEmail) {
        showError?.({ title: 'Validation Error', message: 'Please select a source email account' });
        return;
      }
      if (emailDigestConfig.recipients.length === 0) {
        showError?.({ title: 'Validation Error', message: 'Please add at least one recipient for the digest' });
        return;
      }
    }

    // Validate report config if skill is network-performance-report
    if (selectedSkill === 'network-performance-report') {
      if (reportConfig.recipients.length === 0) {
        showError?.({ title: 'Validation Error', message: 'Please add at least one recipient for the report' });
        return;
      }
    }

    // Validate Slack config if skill is slack-summary
    if (selectedSkill === 'slack-summary') {
      if (slackConfig.scope === 'specific-channels' && slackConfig.channelIds.length === 0) {
        showError?.({ title: 'Validation Error', message: 'Please select at least one Slack channel' });
        return;
      }
    }

    // Validate day selection for weekly schedules
    if (scheduleType === 'weekly' && daysOfWeek.length === 0) {
      showError?.({ title: 'Validation Error', message: 'Please select at least one day of the week' });
      return;
    }

    const schedule: ScheduleConfig = {
      type: scheduleType,
      time,
      timezone,
      daysOfWeek: scheduleType === 'weekly' ? daysOfWeek : undefined,
      dayOfMonth: scheduleType === 'monthly' ? dayOfMonth : undefined,
      startDate: scheduleType === 'once' ? runDate : undefined,
    };

    setSubmitting(true);
    try {
      // Build IO Reconciliation config if applicable
      let ioConfig: IoReconciliationConfig | undefined;
      if (selectedSkill === 'io-reconciliation' && ioTestMode && ioTestEmail.trim()) {
        ioConfig = { testEmail: ioTestEmail.trim().toLowerCase() };
      }

      await createScheduledTask({
        skillName: selectedSkill,
        prompt: SKILLS_REQUIRING_PROMPT[selectedSkill] ? prompt.trim() : '',
        schedule,
        channelName: skillNeedsChannel(selectedSkill) && channelName.trim() ? channelName.trim() : undefined,
        emailDigestConfig: selectedSkill === 'email-digest' ? emailDigestConfig : undefined,
        reportConfig: selectedSkill === 'network-performance-report' ? reportConfig : undefined,
        customSubject: selectedSkill !== 'email-digest' && selectedSkill !== 'slack-summary' && customSubject.trim() ? customSubject.trim() : undefined,
        ioReconciliationConfig: ioConfig,
        slackSummaryConfig: selectedSkill === 'slack-summary' ? slackConfig : undefined,
      });
      onSuccess();
    } catch (err: any) {
      showError?.({ title: 'Error', message: err.message || 'Failed to create scheduled task' });
    } finally {
      setSubmitting(false);
    }
  };

  const styles: { [key: string]: React.CSSProperties } = {
    subtitle: {
      ...ff.hint,
      fontSize: 13,
    },
    form: {
      display: 'flex',
      flexDirection: 'column',
      gap: '1.25rem',
    },
    formGroup: ff.group,
    label: ff.label,
    select: ff.field,
    input: ff.field,
    textarea: ff.textarea,
    skillDescription: ff.hint,
    sectionLabel: ff.label,
    skillGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
      gap: '0.625rem',
    },
    skillCard: {
      position: 'relative' as const,
      textAlign: 'left' as const,
      cursor: 'pointer',
      padding: '0.875rem 0.875rem 0.75rem',
      borderRadius: 'var(--radius-md)',
      border: '1px solid var(--border-1)',
      backgroundColor: 'var(--bg-surface-1)',
      boxShadow: 'var(--shadow-xs)',
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '0.375rem',
      font: 'inherit',
      color: 'inherit',
    },
    skillCardSelected: {
      borderColor: 'var(--cg-saffron-400)',
      backgroundColor: 'var(--accent-2-soft)',
      boxShadow: 'var(--shadow-sm), var(--focus-ring)',
    },
    skillCardIcon: {
      fontSize: '1.5rem',
      lineHeight: 1,
      marginBottom: '0.125rem',
    },
    skillCardEyebrow: ff.microLabel,
    skillCardName: {
      fontSize: '0.9375rem',
      fontWeight: 600,
      color: 'var(--fg-1)',
      lineHeight: 1.25,
    },
    skillCardDesc: {
      fontSize: '0.78125rem',
      color: 'var(--fg-2)',
      lineHeight: 1.4,
      display: '-webkit-box',
      WebkitLineClamp: 3,
      WebkitBoxOrient: 'vertical' as const,
      overflow: 'hidden',
    },
    skillCardCheck: {
      position: 'absolute' as const,
      top: '0.5rem',
      right: '0.625rem',
      width: '18px',
      height: '18px',
      borderRadius: 'var(--radius-pill)',
      backgroundColor: 'var(--cg-saffron-400)',
      color: 'var(--fg-on-accent)',
      fontSize: '0.625rem',
      fontWeight: 700,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      lineHeight: 1,
    },
    skillGridLoading: {
      padding: '1.5rem',
      display: 'flex',
      justifyContent: 'center',
      border: '1px dashed var(--border-1)',
      borderRadius: 'var(--radius-md)',
    },
    scheduleRow: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '1rem',
    },
    daysContainer: {
      display: 'flex',
      flexWrap: 'wrap' as const,
      gap: '0.5rem',
    },
    dayButton: {
      padding: '0.5rem 0.75rem',
      borderRadius: 'var(--radius-md)',
      border: '1px solid var(--border-2)',
      backgroundColor: 'var(--cg-neutral-0)',
      color: 'var(--fg-1)',
      fontSize: '0.75rem',
      fontWeight: 500,
      cursor: 'pointer',
      minWidth: '44px',
      textAlign: 'center' as const,
      transition: 'all 0.15s ease',
    },
    dayButtonSelected: {
      backgroundColor: 'var(--accent)',
      borderColor: 'var(--accent)',
      color: 'var(--fg-on-accent)',
    },
    quickSelectRow: {
      display: 'flex',
      gap: '0.5rem',
      marginTop: '0.5rem',
    },
    quickSelectButton: {
      padding: '0.25rem 0.5rem',
      borderRadius: 'var(--radius-xs)',
      border: 'none',
      backgroundColor: 'var(--bg-surface-3)',
      color: 'var(--fg-2)',
      fontSize: '0.7rem',
      cursor: 'pointer',
    },
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Create Scheduled Task"
      width={680}
      closeOnBackdrop={false}
      closeOnEsc={false}
      footer={
        <>
          <Button variant="ghost" size="md" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="md"
            onClick={handleSubmit}
            disabled={submitting || loadingSkills}
          >
            {submitting ? 'Creating...' : 'Create Task'}
          </Button>
        </>
      }
    >
      <form style={styles.form} onSubmit={handleSubmit}>
          <p style={styles.subtitle}>
            Pick what you'd like the assistant to run on a schedule.
          </p>

          <div style={styles.formGroup}>
            <label style={styles.sectionLabel}>What should run?</label>
            {loadingSkills ? (
              <div style={styles.skillGridLoading}>
                <Spinner label="Loading available tasks…" />
              </div>
            ) : (
              <div style={styles.skillGrid} role="radiogroup" aria-label="Choose a task">
                {skills.map((skill) => {
                  const display = getSkillDisplay(skill.name, skill.description);
                  const isSelected = selectedSkill === skill.name;
                  return (
                    <button
                      key={skill.name}
                      type="button"
                      role="radio"
                      aria-checked={isSelected}
                      onClick={() => setSelectedSkill(skill.name)}
                      className="cg-card--interactive"
                      style={{
                        ...styles.skillCard,
                        ...(isSelected ? styles.skillCardSelected : {}),
                      }}
                    >
                      {isSelected && <span style={styles.skillCardCheck}>✓</span>}
                      <span style={styles.skillCardIcon} aria-hidden="true">
                        {display.icon}
                      </span>
                      <span style={styles.skillCardEyebrow}>{display.category}</span>
                      <span style={styles.skillCardName}>{display.displayName}</span>
                      <span style={styles.skillCardDesc}>{display.shortDescription}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Per-task instructions — only for skills that are ad-hoc by nature */}
          {SKILLS_REQUIRING_PROMPT[selectedSkill] && (
            <div style={styles.formGroup}>
              <label style={styles.label}>
                {SKILLS_REQUIRING_PROMPT[selectedSkill].label}
              </label>
              <p style={styles.skillDescription}>
                {SKILLS_REQUIRING_PROMPT[selectedSkill].helper}
              </p>
              <textarea
                style={styles.textarea}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder={SKILLS_REQUIRING_PROMPT[selectedSkill].placeholder}
                maxLength={10000}
              />
            </div>
          )}

          {/* IO Reconciliation Configuration */}
          {selectedSkill === 'io-reconciliation' && (
            <div style={styles.formGroup}>
              <label style={{
                ...styles.label,
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                cursor: 'pointer',
              }}>
                <input
                  type="checkbox"
                  checked={ioTestMode}
                  onChange={(e) => {
                    setIoTestMode(e.target.checked);
                    if (!e.target.checked) setIoTestEmail('');
                  }}
                  style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                />
                Test Mode
              </label>
              <p style={styles.skillDescription}>
                When enabled, the result email is sent to your test address instead of the ad ops team.
              </p>
              {ioTestMode && (
                <input
                  style={{ ...styles.input, marginTop: '0.5rem' }}
                  type="email"
                  value={ioTestEmail}
                  onChange={(e) => setIoTestEmail(e.target.value)}
                  placeholder="Enter test email address"
                />
              )}
            </div>
          )}

          {/* Channel Name - only shown for skills that need it */}
          {selectedSkill && skillNeedsChannel(selectedSkill) && (
            <div style={styles.formGroup}>
              <label style={styles.label}>Channel Name (Optional)</label>
              <input
                style={styles.input}
                type="text"
                value={channelName}
                onChange={(e) => setChannelName(e.target.value)}
                placeholder="Enter YouTube channel name if applicable"
              />
            </div>
          )}

          {/* Custom Email Subject - shown for tasks that don't provide their own subject field */}
          {selectedSkill && selectedSkill !== 'email-digest' && selectedSkill !== 'slack-summary' && (
            <div style={styles.formGroup}>
              <label style={styles.label}>Email Subject (optional)</label>
              <input
                style={styles.input}
                type="text"
                placeholder="Custom email subject line"
                value={customSubject}
                onChange={(e) => setCustomSubject(e.target.value)}
                maxLength={200}
              />
              <p style={styles.skillDescription}>
                Leave blank to use the default subject.
              </p>
            </div>
          )}

          {/* Email Digest Configuration - only shown for email-digest skill */}
          {selectedSkill === 'email-digest' && (
            <EmailDigestConfig
              config={emailDigestConfig}
              onChange={setEmailDigestConfig}
            />
          )}

          {/* Report Configuration - only shown for network-performance-report skill */}
          {selectedSkill === 'network-performance-report' && (
            <ReportConfig
              config={reportConfig}
              onChange={setReportConfig}
            />
          )}

          {/* Slack Summary Configuration - only shown for slack-summary skill */}
          {selectedSkill === 'slack-summary' && (
            <SlackConfig
              config={slackConfig}
              onChange={setSlackConfig}
              userEmail={auth.currentUser?.email || ''}
            />
          )}

          <div style={styles.formGroup}>
            <label style={styles.label}>Schedule Type</label>
            <select
              style={styles.select}
              value={scheduleType}
              onChange={(e) => setScheduleType(e.target.value as any)}
            >
              <option value="once">Run Once</option>
              <option value="weekly">Select Days</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>

          <div style={styles.scheduleRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Time</label>
              <input
                style={styles.input}
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Timezone</label>
              <select
                style={styles.select}
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
              >
                {TIMEZONES.map((tz) => (
                  <option key={tz.value} value={tz.value}>
                    {tz.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {scheduleType === 'once' && (
            <div style={styles.formGroup}>
              <label style={styles.label}>Run Date</label>
              <input
                style={styles.input}
                type="date"
                value={runDate}
                min={getLocalDateString()}
                onChange={(e) => setRunDate(e.target.value)}
              />
            </div>
          )}

          {scheduleType === 'weekly' && (
            <div style={styles.formGroup}>
              <label style={styles.label}>Days of Week</label>
              <div style={styles.daysContainer}>
                {DAYS_OF_WEEK.map((day) => (
                  <button
                    key={day.value}
                    type="button"
                    style={{
                      ...styles.dayButton,
                      ...(daysOfWeek.includes(day.value) ? styles.dayButtonSelected : {}),
                    }}
                    onClick={() => toggleDayOfWeek(day.value)}
                    title={day.fullLabel}
                  >
                    {day.label}
                  </button>
                ))}
              </div>
              <div style={styles.quickSelectRow}>
                <button
                  type="button"
                  style={styles.quickSelectButton}
                  onClick={() => setDaysOfWeek([1, 2, 3, 4, 5])}
                >
                  Weekdays
                </button>
                <button
                  type="button"
                  style={styles.quickSelectButton}
                  onClick={() => setDaysOfWeek([0, 6])}
                >
                  Weekends
                </button>
                <button
                  type="button"
                  style={styles.quickSelectButton}
                  onClick={() => setDaysOfWeek([0, 1, 2, 3, 4, 5, 6])}
                >
                  Every Day
                </button>
              </div>
            </div>
          )}

          {scheduleType === 'monthly' && (
            <div style={styles.formGroup}>
              <label style={styles.label}>Day of Month</label>
              <select
                style={styles.select}
                value={dayOfMonth}
                onChange={(e) => setDayOfMonth(Number(e.target.value))}
              >
                {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => {
                  // Proper ordinal suffix: 1st, 2nd, 3rd, 4th-20th, 21st, 22nd, 23rd, 24th-30th, 31st
                  const suffix = (day % 10 === 1 && day !== 11) ? 'st' :
                                 (day % 10 === 2 && day !== 12) ? 'nd' :
                                 (day % 10 === 3 && day !== 13) ? 'rd' : 'th';
                  return (
                    <option key={day} value={day}>
                      {day}{suffix}
                    </option>
                  );
                })}
              </select>
            </div>
          )}
      </form>
    </Modal>
  );
};

export default CreateScheduledTaskModal;
